// Règles de répartition des prix passagers (PROPORTION du tarif adulte, PAS une réduction).
// Ce sont des poids : chaque passager paie (poids × tarif adulte de référence).
//
//   ADULT       = 100 % du tarif adulte de référence  => poids 1.00
//   CHILD       =  15 %                                => poids 0.15
//   HELD_INFANT =  10 %                                => poids 0.10
//
// Algorithme :
//   1. poids_total    = Σ (poids × nb passagers du type)
//   2. tarifAdulteRef = total / poids_total
//   3. prix passager  = poids × tarifAdulteRef
//   4. Correction d'arrondi (méthode des plus grands restes) pour que la somme
//      des prix passagers arrondis soit EXACTEMENT égale au total.

export const PASSENGER_WEIGHTS = {
  ADULT: 1.0,
  CHILD: 0.15,
  HELD_INFANT: 0.1,
};

const weightOf = (type) => PASSENGER_WEIGHTS[type] ?? 1.0;

/**
 * Répartit un montant total entre passagers selon leur type (poids).
 *
 * @param {number} total Montant total à répartir (ex : 1_100_000).
 * @param {Array<string>} types Types des passagers (ex : ['ADULT','ADULT','CHILD','HELD_INFANT']).
 * @returns {Array<{type: string, amount: number}>} Prix entier par passager (par ordre d'entrée).
 */
export function distributePrice(total, types) {
  const list = types || [];
  const n = list.length;
  if (n === 0) return [];

  total = Number.isFinite(Number(total)) ? Math.round(Number(total)) : 0;

  const weights = list.map(weightOf);
  const totalWeight = weights.reduce((sum, w) => sum + w, 0);

  if (totalWeight <= 0) {
    return list.map((type) => ({ type, amount: 0 }));
  }

  const referenceUnit = total / totalWeight;

  // Montant brut + arrondi à l'unité (F) pour chaque passager
  const items = list.map((type, i) => {
    const raw = weights[i] * referenceUnit;
    const floored = Math.floor(raw);
    return { type, raw, floored, decimal: raw - floored };
  });

  let remainder = total - items.reduce((sum, it) => sum + it.floored, 0);

  // Méthode des plus grands restes : on ajoute 1 F aux passagers dont la partie
  // décimale est la plus grande, jusqu'à retomber pile sur le total.
  const sorted = items
    .map((it, i) => ({ i, decimal: it.decimal }))
    .sort((a, b) => b.decimal - a.decimal || a.i - b.i);

  let k = 0;
  while (remainder > 0 && k < n * (n + 1)) {
    const idx = sorted[k % n].i;
    items[idx].floored += 1;
    remainder -= 1;
    k += 1;
  }

  // Sécurité : si par erreur la répartition dépasse le total, on retire 1 F aux
  // passagers dont la décimale (non arrondie) est la plus petite (donc les moins
  // "méritants"). Cas théoriquement impossible avec des poids >= 0.
  if (remainder < 0) {
    const sortedAsc = items
      .map((it, i) => ({ i, decimal: it.decimal }))
      .sort((a, b) => a.decimal - b.decimal || a.i - b.i);
    let j = 0;
    while (remainder < 0 && j < n * (n + 1)) {
      const idx = sortedAsc[j % n].i;
      if (items[idx].floored > 0) {
        items[idx].floored -= 1;
        remainder += 1;
      }
      j += 1;
    }
  }

  return items.map((it) => ({ type: it.type, amount: it.floored }));
}

/**
 * Variante groupée par type de passager (pratique pour l'affichage d'un décompte).
 *
 * @param {number} total Montant total à répartir.
 * @param {Array<string>} types Types des passagers.
 * @returns {Array<{type: string, count: number, total: number, unit: number}>}
 */
export function groupPriceBreakdown(total, types) {
  const assigned = distributePrice(total, types);
  const groups = {};
  for (const item of assigned) {
    if (!groups[item.type]) {
      groups[item.type] = { type: item.type, count: 0, total: 0, unit: 0 };
    }
    groups[item.type].count += 1;
    groups[item.type].total += item.amount;
    groups[item.type].unit = item.amount;
  }
  return Object.values(groups);
}